import isEmpty from 'lodash/isEmpty';

import BaseApi, { API_VERSION, cleanParams } from '@lumapps/base-api';
import {
    ApiKeyConfiguration,
    Connector,
    CONNECTOR_PROTOCOL,
    Oauth2Configuration,
    CONNECTOR_STATUS,
    BasicConfiguration,
} from '@lumapps/marketplace/sdk';
import { ObjectValues } from '@lumapps/utils/types/ObjectValues';

import { CONNECTOR_SORT, ConnectorAuthorizeResponse, ConnectorListResponse, PartnerTemplateValues } from '../types';

export interface FetchConnectorListFilters {
    hasPartner?: boolean;
    name?: string;
    partnerIds?: string;
    protocol?: ObjectValues<typeof CONNECTOR_PROTOCOL>;
    providerType?: string;
    sort?: CONNECTOR_SORT;
    statuses?: ObjectValues<typeof CONNECTOR_STATUS>[];
    extensionId?: string;
}

export interface FetchConnectorListAPIParams extends FetchConnectorListFilters {
    cursor?: string;
    maxResults?: number;
}

/**
 * Base API instance for connectors.
 */
export const fetchConnectorListAPI = new BaseApi({ path: 'connectors', version: API_VERSION.v2 });

/**
 * Fetch the connector list.
 */
export const fetchConnectorList = (params: FetchConnectorListAPIParams, fetchKey?: string, signal?: AbortSignal) => {
    const parameters = {
        ...params,
        name: !isEmpty(params.name) ? params.name : undefined,
        statuses: params.statuses && !isEmpty(params.statuses) ? params.statuses.join(',') : undefined,
    };

    return fetchConnectorListAPI.get<ConnectorListResponse>(
        '',
        {
            params: {
                ...cleanParams({
                    ...parameters,
                }),
                hasPartner: params.hasPartner,
            },
            signal,
        },
        undefined,
        true,
        fetchKey,
    );
};

export const cancelGetConnectorList = (fetchKey: string) => {
    return fetchConnectorListAPI.cancel('', undefined, fetchKey);
};

interface CreateConnectorCommonParams {
    baseUrl: string;
    name: string;
    providerType: string;
}

interface CreateOAuthConfiguration extends Omit<Oauth2Configuration, 'clientSecret' | 'compliance'> {
    clientSecret?: string;
    compliance: Omit<Oauth2Configuration['compliance'], 'scopeSeparator'> & { scopeSeparator?: string };
}
export interface CreateOauth2ConnectorParams extends CreateConnectorCommonParams {
    configuration: CreateOAuthConfiguration;
    protocol: 'oauth2';
}

export interface CreateApiKeyConnectorParams extends CreateConnectorCommonParams {
    configuration: Omit<ApiKeyConfiguration, 'apiKey'> & { apiKey?: string };
    protocol: 'api_key';
}

export interface CreateBasicConnectorParams extends CreateConnectorCommonParams {
    configuration: BasicConfiguration;
    protocol: 'basic';
}

export type CreateConnectorParams =
    | CreateOauth2ConnectorParams
    | CreateApiKeyConnectorParams
    | CreateBasicConnectorParams;

export type CreateFromPartnerConnectorParams = {
    partnerConnectorId: string;
    name: string;
    values: PartnerTemplateValues;
};

/**
 * Create a connector.
 */
export const createConnector = (params: CreateConnectorParams | CreateFromPartnerConnectorParams) =>
    fetchConnectorListAPI.post<Connector>('', { ...cleanParams(params) });

/**
 * Edit a connector.
 */
export const editConnector = ({ connectorId, params }: { connectorId: string; params: CreateConnectorParams }) =>
    fetchConnectorListAPI.put<Connector>(connectorId, { ...cleanParams(params) });

/**
 * Fetch a connector by its id.
 */
export const getConnector = (connectorId: Connector['id']) => fetchConnectorListAPI.get<Connector>(connectorId);

/**
 * Delete a connector by its id.
 */
export const deleteConnector = (connectorId: Connector['id']) => fetchConnectorListAPI.delete(connectorId);

/**
 * Fetch authorize URL.
 */
export const getAuthorizeUrl = (connectorId: Connector['id']) =>
    fetchConnectorListAPI.get<ConnectorAuthorizeResponse>(`${connectorId}/authorize`);

/**
 * De-authorize connector
 */
export const deauthorizeConnector = (connectorId: Connector['id']) =>
    fetchConnectorListAPI.post<ConnectorAuthorizeResponse>(`${connectorId}/deauthorize`, {});
